import { autoinject } from 'aurelia-framework';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { ITrackedUser, TrackingService } from 'app/core/services/tracking-service';
import { TickService, ITickEvent, TickHandler } from 'app/shared/services/event-services';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { State } from 'app/shared/lib/state';
import { buildSnapshot } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { bindable } from 'aurelia-typed-observable-plugin';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { UserTrackerContinueEvent } from 'app/core/lib/events';
import { I18N } from 'aurelia-i18n';

@autoinject
export class UserTracker {

    @bindable.boolean()
    protected isAnonymous: boolean = false;

    @bindable.string()
    protected modelUrl: string = '';

    @bindable.stringNull()
    protected label: string | null = null;

    @bindable.string()
    protected tooltipPosition: string = 'bottom';

    @bindable.object()
    protected users: ITrackedUser[] = [];

    // To determine if the component is for display purposes or will tick to track users
    @bindable.boolean()
    protected isNonTick: boolean = false;

    @bindable.boolean()
    protected showLock: boolean = false;

    // To determine if data is checked out (exclusive to one user)
    @bindable.boolean()
    protected isCheckedOut: boolean = false;

    // To get single user currently checking out data
    @bindable.object()
    protected userCheckedOut: ITrackedUser | null = null;

    // If true, only show user count instead of listing them,
    // do not show icon if there are no tracked users,
    // and announce tracked users as 'Health Plan User' instead of 'Unknown User'
    @bindable.boolean()
    protected isProviderPortal: boolean = false;

    protected error: boolean = false;

    protected readonly state: State = new State();

    private lastUpdate: number;
    private readonly updateDuration: number;
    private readonly updateUsers?: TickHandler;
    protected isContinuation: boolean = false;
    private subscriptions: Array<Subscription> = [];

    constructor(
        private readonly env: EnvironmentService,
        private readonly toaster: Toaster,
        private readonly ticker: TickService,
        private readonly trackingService: TrackingService,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        private readonly logger: AppLogger) {
        this.logger.source = this;

        this.updateDuration = this.env.config.tracking.updateIntervalSeconds * 1000; // seconds to milliseconds
        this.lastUpdate = 0; // trigger update immediately

        if(!this.isNonTick) {
            this.updateUsers = (e: ITickEvent) => {
                if(e.time > this.lastUpdate + this.updateDuration) {
                    if(!env.user.isLoggedIn) {
                        this.reset(false);
                    }
                    else if(!this.state.busy && env.isAppVisible() && this.modelUrl) {
                        this.track();
                    }
                }
            }
        }
    }

    protected bind(): void {
        if(!this.isNonTick && this.updateUsers) {
            this.track();
            this.ticker.addEventListener('tick', this.updateUsers);
        }

        this.subscriptions.push(
            this.events.subscribe(UserTrackerContinueEvent, () => {
                this.isContinuation = true;
            })
        );
    }

    protected unbind(): void {
        if(!this.isNonTick && this.updateUsers) {
            this.ticker.removeEventListener('tick', this.updateUsers);
            if(!this.isContinuation) {
                this.untrack();
            }
        }
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    protected modelUrlChanged(newValue: string, oldValue: string) {
        if(!this.isNonTick) {
            if(newValue) {
                this.untrack(oldValue);
                this.reset(false);
            }
            else {
                this.track(newValue);
            }
        }
    }

    private reset(load: boolean = true): void {
        this.users = [];
        this.lastUpdate = 0; // trigger update immediately
        if(load) {
            this.track();
        }
    }

    private async untrack(modelUrl: string = this.modelUrl): Promise<void> {
        try {
            this.state.beginLoad();
            if(!this.env.user.isLoggedIn) { return; }
            await this.trackingService.trackUsers(modelUrl, false);
            this.users = [];
            this.error = false;
        }
        catch(error) {
            this.logger.error('Error Tracking Users', error);
            this.error = true;
        }
        finally {
            this.state.endLoad();
        }
    }

    private async track(modelUrl: string = this.modelUrl): Promise<void> {
        try {
            this.state.beginLoad();
            if(!this.env.user.isLoggedIn) { return; }

            //When isAnonymous = true, do not track the user within the auth
            const response = await this.trackingService.trackUsers(modelUrl, !this.isAnonymous);
            if(response.isSuccessful) {
                const oldUsers = this.users;
                const oldSnapshot = buildSnapshot(this.users);
                this.users = response.result && response.result.users || [];
                const newSnapshot = buildSnapshot(this.users);

                if(oldSnapshot !== newSnapshot && !this.isAnonymous) {
                    this.showToasts(oldUsers, this.users);
                }
            }

            this.lastUpdate = Date.now();
            this.error = false;
        }
        catch(error) {
            this.logger.error('Error Tracking Users', error);
            this.error = true;
        }
        finally {
            this.state.endLoad();
        }
    }

    private showToasts(oldUsers: ITrackedUser[], newUsers: ITrackedUser[]): void {
        const defaultUserLabel = this.isProviderPortal ? this.i18n.tr('cd:healthPlan.user.$') : this.i18n.tr('ui:labels.unknown', { what: this.i18n.tr('ui:labels.user.$') });
        const entered: ITrackedUser[] = [];
        const exited: ITrackedUser[] = [];
        for(const newUser of newUsers) {
            if(!oldUsers.find(u => u.id === newUser.id)) {
                entered.push(newUser);
            }
        }
        for(const oldUser of oldUsers) {
            if(!newUsers.find(u => u.id === oldUser.id)) {
                exited.push(oldUser);
            }
        }
        const enteredNames: string[] = entered.map((user) => user.name?.display || defaultUserLabel);
        const exitedNames: string[] = exited.map((user) => user.name?.display || defaultUserLabel);

        // Find the earliest tracking date/time value for all newly entered users
        const onDateTime = entered
            .filter((user) => user.tracking?.trackedTimestamp)
            .map((user) => new DateTimeValue(user.tracking.trackedTimestamp!))
            .sort((a, b) => a.compareTo(b))[0]?.value || this.env.now();

        this.showToast(false, 'ui:messages.tracking.userEnteredOn', 'ui:messages.tracking.usersEnteredOn', enteredNames, onDateTime);
        this.showToast(true, 'ui:messages.tracking.userExited', 'ui:messages.tracking.usersExited', exitedNames, onDateTime);
    }

    private async showToast(exit: boolean, singleKey: string, multipleKey: string, names: string[], onDateTime: string): Promise<void> {
        if(names.length > 0) {
            const toast = (exit ? this.toaster.toast() : this.toaster.info())
                .dismissButton(false)
                .icon(exit ? 'ui:icons.userExited' : 'ui:icons.userEntered')
                .duration('long')
                .defaultResult('dismiss')
                .action('ok', 'OK')
                .message({
                    key: names.length === 1 ? singleKey : multipleKey,
                    count: names.length - 1,
                    replace: { what: this.label, who: names[0], count: names.length - 1, when: onDateTime }
                });
            await toast.show();
        }
    }

}
